/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef } from 'react';
import CatererPdfViewer from './caterer-pdf-viewer';
import closeIcon from '../../../images/icons/Icon2.png';
import '../../../assets/styles/modal.scss';
import '../../../assets/styles/caterer-invoicing.scss';
import {
  FaFileUpload,
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFilePdf,
  FaBolt
} from "react-icons/fa";
import { APIEndpoint } from '../../../utils/axios';
import { IS_DEMO_MODE, DEMO_VENDORS, DEMO_REQUIRED_FIELDS, findDemoScenario, isDuplicateInvoiceId, formatDemoCurrency } from '../../../assets/demo/demoInvoiceData';

const INVOICE_TYPES = [
  { value: 'Service', label: 'Service Invoice' },
  { value: 'Uplifts', label: 'Uplifts Invoice' },
  { value: 'Bulk Deliveries', label: 'Bulk Deliveries' }
];

const PLAIN_FIELD_CONFIG = {
  invoiceNumber: { label: 'Invoice Number', placeholder: 'e.g. CAFS/26-27/08/000000003' },
  sector: { label: 'Flight Sector', placeholder: 'e.g. BOM-DEL' },
  poNumber: { label: 'Purchase Order Number', placeholder: 'e.g. PO-2026-0891' },
  locationCode: { label: 'Station Code (3-letter IATA)', placeholder: 'e.g. HYD', maxLength: 3 }
};

const FIELD_HINT_LABELS = {
  invoiceNumber: 'Invoice Number',
  locationCode: 'Station Code',
  startDate: 'Date Range',
  endDate: 'Date Range',
  sector: 'Flight Sector',
  poNumber: 'Purchase Order Number'
};

const describeRequiredFields = (fields) => {
  const seen = new Set();
  const labels = [];
  for (const field of fields) {
    const label = FIELD_HINT_LABELS[field] || field;
    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels.join(', ');
};

export const CreatecatererInvoice = (props) => {
  const { onClose, onSubmit, onInvoiceCreated, existingDemoInvoices = [] } = props;

  const [invoiceType, setInvoiceType] = useState('');
  const [requiredFields, setRequiredFields] = useState([]);

  const [selectedPdf, setSelectedPdf] = useState(null);
  const fileInput = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const [isExtracting, setIsExtracting] = useState(false);
  const [extractProgressText, setExtractProgressText] = useState('Extracting invoice with AI...');
  const [extractError, setExtractError] = useState(null);

  // Result of the pre-commit preview: nothing here is persisted server-side
  // until Submit — see POST /api/caterer-invoices/submit.
  const [previewResult, setPreviewResult] = useState(null);

  // Manual corrections for whatever preview reported as missing
  const [manualFields, setManualFields] = useState({});
  const [vendorOptions, setVendorOptions] = useState(null); // lazy-loaded fallback list

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const resetExtractionState = () => {
    setPreviewResult(null);
    setManualFields({});
    setExtractError(null);
    setSubmitError(null);
  };

  const handleInvoiceTypeChange = async (e) => {
    const newType = e.target.value;
    setInvoiceType(newType);
    setSelectedPdf(null);
    resetExtractionState();
    setRequiredFields([]);

    if (!newType) return;

    if (IS_DEMO_MODE) {
      setRequiredFields(DEMO_REQUIRED_FIELDS[newType] || []);
      return;
    }

    try {
      const resp = await APIEndpoint.get(`caterer-invoices/required-fields/${newType}`);
      setRequiredFields(resp?.requiredFields || []);
    } catch (err) {
      console.warn('Failed to load required-fields rule:', err.message);
      setRequiredFields([]);
    }
  };

  const handlePdfUpload = (e) => {
    if (e.currentTarget.files && e.currentTarget.files[0]) {
      setSelectedPdf(e.currentTarget.files[0]);
      resetExtractionState();
    }
  };

  const decrementPageNo = () => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  };

  const incrementPageNo = () => {
    if (pageNumber < numPages) setPageNumber(pageNumber + 1);
  };

  // Runs (or re-runs) the pre-commit extraction preview. Per data-integrity
  // decision: this always fully replaces any prior extraction/manual entries
  // — nothing is merged forward from a previous attempt.
  const handleExtractData = async () => {
    if (!invoiceType || !selectedPdf) return;
    setIsExtracting(true);
    resetExtractionState();
    setExtractProgressText('Uploading PDF...');

    // Demo simulation: a handful of sample PDFs (see /demo-invoices) are
    // recognized by filename and short-circuit straight to canned results,
    // with no backend involved at all — see demoInvoiceData.js.
    const demoScenario = findDemoScenario(selectedPdf.name);
    if (demoScenario) {
      if (demoScenario.invoiceType !== invoiceType) {
        setIsExtracting(false);
        setExtractError(`This sample PDF is a "${demoScenario.invoiceType}" invoice — select "${demoScenario.invoiceType}" as the invoice type above, then try again.`);
        return;
      }
      setExtractProgressText('Running AI extraction and cross-checking supplier/station records...');
      const duplicateInvoiceId = isDuplicateInvoiceId(demoScenario.finalInvoice.invoiceId, existingDemoInvoices)
        ? demoScenario.finalInvoice.invoiceId
        : null;
      setTimeout(() => {
        setPreviewResult({
          ...demoScenario.preview,
          pdfFileName: selectedPdf.name,
          pdfFilePath: null,
          __demo: true,
          __demoFinalInvoice: demoScenario.finalInvoice,
          __duplicateInvoiceId: duplicateInvoiceId
        });
        setIsExtracting(false);
      }, 1400);
      return;
    }

    if (IS_DEMO_MODE) {
      setIsExtracting(false);
      setExtractError('Demo mode is enabled — please upload one of the sample invoice PDFs provided for this demo (see /demo-invoices).');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedPdf);
      formData.append('invoiceType', invoiceType);

      const uploadResp = await APIEndpoint.post('caterer-invoices/extract-preview', formData, {
        'Content-Type': 'multipart/form-data'
      });

      if (!uploadResp || !uploadResp.jobId) {
        setIsExtracting(false);
        setExtractError('Extraction did not start — please retry.');
        return;
      }

      setExtractProgressText('Running AI extraction and cross-checking supplier/station records...');
      const jobId = uploadResp.jobId;

      let attempts = 0;
      const maxAttempts = 40;
      const pollTimer = setInterval(async () => {
        attempts++;
        try {
          const statusResp = await APIEndpoint.get(`caterer-invoices/extract-preview/${jobId}/status`);

          if (statusResp && statusResp.status === 'failed') {
            clearInterval(pollTimer);
            setIsExtracting(false);
            setExtractError(statusResp.error || 'Extraction failed for this PDF. Please retry, or verify it is a text-layer PDF.');
            return;
          }

          if (statusResp && statusResp.status === 'completed') {
            clearInterval(pollTimer);
            setPreviewResult(statusResp);
            setIsExtracting(false);
            return;
          }

          if (attempts >= maxAttempts) {
            clearInterval(pollTimer);
            setIsExtracting(false);
            setExtractError('Extraction is taking longer than expected. Please retry.');
          }
        } catch (pollErr) {
          console.warn('Preview status polling failed:', pollErr.message);
          if (attempts >= maxAttempts) {
            clearInterval(pollTimer);
            setIsExtracting(false);
            setExtractError('Lost connection while checking extraction status. Please retry.');
          }
        }
      }, 1500);
    } catch (err) {
      console.error('extract-preview failed:', err.message);
      setIsExtracting(false);
      setExtractError('Extraction service unavailable — please retry in a moment.');
    }
  };

  const loadVendorOptions = async () => {
    if (vendorOptions) return;

    if (IS_DEMO_MODE) {
      setVendorOptions(DEMO_VENDORS);
      return;
    }

    try {
      const resp = await APIEndpoint.get('warehouses/vendors/enumerate');
      setVendorOptions(resp?.items || []);
    } catch (err) {
      console.warn('Failed to load vendor list:', err.message);
      setVendorOptions([]);
    }
  };

  // Fields still missing after folding in manual corrections on top of the
  // preview's own missingFields list.
  const getOutstandingFields = () => {
    if (!previewResult) return [];
    return (previewResult.missingFields || []).filter(field => {
      if (field === 'caterer') return !manualFields.selectedVendorCode;
      return !manualFields[field];
    });
  };

  const outstandingFields = getOutstandingFields();
  const isDuplicate = Boolean(previewResult?.__duplicateInvoiceId);
  const isReadyToSubmit = Boolean(previewResult) && outstandingFields.length === 0 && !isDuplicate;

  const handleSubmit = async () => {
    if (!isReadyToSubmit || !previewResult) return;
    setIsSubmitting(true);
    setSubmitError(null);

    if (previewResult.__demo) {
      setTimeout(() => {
        const newInvoice = {
          ...previewResult.__demoFinalInvoice,
          id: `DEMO-${Date.now()}`,
          selectedPdf
        };
        setIsSubmitting(false);
        if (onInvoiceCreated) onInvoiceCreated(newInvoice);
        if (onSubmit) onSubmit();
      }, 900);
      return;
    }

    if (IS_DEMO_MODE) {
      // Should be unreachable — handleExtractData refuses to populate
      // previewResult from the real API while demo mode is on — but guard
      // it anyway so a real submit call can never fire in this mode.
      setIsSubmitting(false);
      setSubmitError('Demo mode is enabled — please upload one of the sample invoice PDFs provided for this demo.');
      return;
    }

    const finalExtractedData = {
      ...previewResult.extractedData,
      ...(manualFields.invoiceNumber ? { invoiceNumber: manualFields.invoiceNumber } : {}),
      ...(manualFields.locationCode ? { locationCode: manualFields.locationCode } : {}),
      ...(manualFields.sector ? { sector: manualFields.sector } : {}),
      ...(manualFields.poNumber ? { poNumber: manualFields.poNumber } : {}),
      ...(manualFields.startDate ? { startDate: manualFields.startDate } : {}),
      ...(manualFields.endDate ? { endDate: manualFields.endDate } : {})
    };

    try {
      const resp = await APIEndpoint.post('caterer-invoices/submit', {
        invoiceType,
        extractedData: finalExtractedData,
        pdfFilePath: previewResult.pdfFilePath,
        pdfFileName: previewResult.pdfFileName,
        selectedVendorCode: manualFields.selectedVendorCode || undefined
      });

      if (resp && resp.success) {
        setIsSubmitting(false);
        if (onInvoiceCreated) {
          onInvoiceCreated(resp.item || { id: resp.id, status: resp.status, invoiceType });
        }
        if (onSubmit) onSubmit();
        return;
      }

      setIsSubmitting(false);
      setSubmitError((resp && resp.message) || 'Submission failed — please retry.');
    } catch (err) {
      setIsSubmitting(false);
      let errData = null;
      try {
        errData = JSON.parse(err.message)?.data;
      } catch (parseErr) {
        // err.message wasn't the expected {status, data} JSON — fall through
      }
      const missing = errData?.missingFields;
      setSubmitError(
        missing?.length
          ? `Still missing: ${missing.join(', ')}`
          : (errData?.message || 'Submission failed — please retry.')
      );
    }
  };

  const setManualField = (name, value) => {
    setManualFields(prev => ({ ...prev, [name]: value }));
  };

  const renderPlainField = (field) => {
    const config = PLAIN_FIELD_CONFIG[field];
    if (!config) return null;
    return (
      <div className='caterer-missing-fields__field' key={field}>
        <label className='caterer-field-label'>
          {config.label} <span className='caterer-required-mark'>*</span>
        </label>
        <input
          type='text'
          maxLength={config.maxLength}
          className='edit-input caterer-field--full'
          placeholder={config.placeholder}
          value={manualFields[field] || ''}
          onChange={(e) => setManualField(field, field === 'locationCode' ? e.target.value.toUpperCase() : e.target.value)}
        />
      </div>
    );
  };

  const renderDateRangeField = () => {
    const needsStart = outstandingFields.includes('startDate');
    const needsEnd = outstandingFields.includes('endDate');
    if (!needsStart && !needsEnd) return null;
    return (
      <div className='caterer-missing-fields__field' key='dateRange'>
        <label className='caterer-field-label'>
          Billing / Flight Date Range (From - To) <span className='caterer-required-mark'>*</span>
        </label>
        <div className='flexRow alignItemsCenter gap10'>
          <input
            type='date'
            className='edit-input'
            value={manualFields.startDate || ''}
            onChange={(e) => setManualField('startDate', e.target.value)}
          />
          <span>to</span>
          <input
            type='date'
            className='edit-input'
            value={manualFields.endDate || ''}
            onChange={(e) => setManualField('endDate', e.target.value)}
          />
        </div>
      </div>
    );
  };

  const renderCatererField = () => {
    if (!outstandingFields.includes('caterer')) return null;
    return (
      <div className='caterer-missing-fields__field' key='caterer'>
        <label className='caterer-field-label'>
          Couldn't match the extracted GSTIN/name to a registered caterer — select one <span className='caterer-required-mark'>*</span>
        </label>
        <select
          className='select-input caterer-field--full'
          value={manualFields.selectedVendorCode || ''}
          onFocus={loadVendorOptions}
          onChange={(e) => setManualField('selectedVendorCode', e.target.value)}
        >
          <option value=''>-- Select Caterer --</option>
          {(vendorOptions || []).map(v => (
            <option key={v.id} value={v.code}>{v.code} — {v.name}</option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className='overlay' role='dialog'>
      <div className='dialog-caterer sm-scrollbar'>
        <div className='dialog__content'>
          {/* MODAL HEADER */}
          <div className='dialog-header flexRow alignItemsCenter'>
            <div className='flexRow alignItemsCenter gap10'>
              <div className='caterer-header__icon'>
                <FaFileUpload size={18} />
              </div>
              <div>
                <h2 className='dialog__title'>Upload Caterer Invoice</h2>
                <div className='caterer-header__meta'>
                  AI-assisted ingestion with automatic supplier/station cross-verification
                </div>
              </div>
            </div>
            <button className='close-btn-ht' onClick={onClose}>
              <img src={closeIcon} alt='close' />
            </button>
          </div>

          {/* MODAL BODY (SPLIT-SCREEN) */}
          <div className='dialog__body_container'>
            <div className='caterer-split'>

              {/* LEFT PANEL: GATED SINGLE-FLOW FORM */}
              <div className='caterer-split__form sm-scrollbar'>

                <h3 className='caterer-section-title'>1. Select Invoice Type</h3>
                <div className='caterer-field-grid' style={{ gridTemplateColumns: '1fr', marginBottom: '24px' }}>
                  <select
                    className='select-input'
                    value={invoiceType}
                    onChange={handleInvoiceTypeChange}
                  >
                    <option value=''>-- Select Invoice Type --</option>
                    {INVOICE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {invoiceType && requiredFields.length > 0 && (
                  <div className='caterer-header__meta' style={{ marginTop: '-16px', marginBottom: '20px' }}>
                    This invoice type needs: <strong>{describeRequiredFields(requiredFields)}</strong>, plus a matching supplier — we'll ask for anything the PDF doesn't already have after extraction.
                  </div>
                )}

                <h3 className='caterer-section-title'>2. Upload Invoice PDF</h3>
                <div style={{ marginBottom: '24px' }}>
                  <input
                    ref={fileInput}
                    onChange={handlePdfUpload}
                    type='file'
                    accept='.pdf'
                    disabled={!invoiceType}
                    style={{ display: 'none' }}
                  />
                  <div
                    onClick={() => invoiceType && fileInput.current?.click()}
                    className={`caterer-dropzone ${selectedPdf ? 'caterer-dropzone--filled' : ''}`}
                    style={!invoiceType ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    <FaFilePdf size={42} style={{ marginBottom: '10px', opacity: selectedPdf ? 1 : 0.5 }} />
                    {selectedPdf ? (
                      <div>
                        <div className='caterer-dropzone__title'>{selectedPdf.name}</div>
                        <div className='caterer-dropzone__subtitle'>
                          {(selectedPdf.size / 1024).toFixed(1)} KB • Click to change document
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className='caterer-dropzone__title'>
                          {invoiceType ? 'Click or Drag & Drop PDF Invoice here' : 'Select an invoice type first'}
                        </div>
                        <div className='caterer-dropzone__subtitle'>
                          Supports digital text-layer PDF invoices from caterers
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {extractError && (
                  <div className='caterer-alert caterer-alert--error'>
                    <FaExclamationTriangle size={20} />
                    <div className='caterer-alert__desc'>{extractError}</div>
                  </div>
                )}

                <div className='flexRow justifyContentFlexEnd margBottom10'>
                  <button
                    className={`submit-button ${(!invoiceType || !selectedPdf || isExtracting) ? 'button_inrt' : ''}`}
                    disabled={!invoiceType || !selectedPdf || isExtracting}
                    onClick={handleExtractData}
                  >
                    <FaBolt /> {isExtracting ? extractProgressText : (previewResult ? 'Re-Extract Data' : 'Extract Data')}
                  </button>
                </div>

                {previewResult && (
                  <>
                    <h3 className='caterer-section-title'>3. Review & Complete</h3>

                    {isDuplicate ? (
                      <div className='caterer-alert caterer-alert--error'>
                        <FaExclamationTriangle size={24} />
                        <div>
                          <div className='caterer-alert__title'>Possible Duplicate Invoice</div>
                          <div className='caterer-alert__desc'>
                            Invoice <strong>{previewResult.__duplicateInvoiceId}</strong> from {previewResult.resolvedVendor?.vendorName || previewResult.extractedData?.catererName} already exists in the system. Submission is blocked to prevent double-billing — review or reject the existing entry first.
                          </div>
                        </div>
                      </div>
                    ) : isReadyToSubmit ? (
                      <div className='caterer-alert caterer-alert--success'>
                        <FaCheckCircle size={24} />
                        <div>
                          <div className='caterer-alert__title'>All Required Details Available</div>
                          <div className='caterer-alert__desc'>You can submit this invoice for verification.</div>
                        </div>
                      </div>
                    ) : (
                      <div className='caterer-alert caterer-alert--warning'>
                        <FaExclamationTriangle size={24} />
                        <div>
                          <div className='caterer-alert__title'>Missing Mandatory Details Detected</div>
                          <div className='caterer-alert__desc'>Please fill the field(s) below before submitting.</div>
                        </div>
                      </div>
                    )}

                    <div className='caterer-summary-card'>
                      <div className='caterer-summary-card__label'>Extracted from PDF Text Layer</div>
                      <div className='caterer-summary-card__grid'>
                        <div>
                          <span className='caterer-summary-card__key'>Invoice Number: </span>
                          <strong>{previewResult.extractedData?.invoiceNumber || '—'}</strong>
                        </div>
                        <div>
                          <span className='caterer-summary-card__key'>Invoice Date: </span>
                          <strong>{previewResult.extractedData?.invoiceDate ? String(previewResult.extractedData.invoiceDate).slice(0, 10) : '—'}</strong>
                        </div>
                        <div>
                          <span className='caterer-summary-card__key'>Caterer: </span>
                          <strong>
                            {previewResult.resolvedVendor?.matchStatus === 'matched'
                              ? `${previewResult.resolvedVendor.vendorName} (matched)`
                              : (previewResult.extractedData?.catererName || '—')}
                          </strong>
                        </div>
                        <div>
                          <span className='caterer-summary-card__key'>Billed Total: </span>
                          <strong>{formatDemoCurrency(previewResult.extractedData?.total)}</strong>
                        </div>
                        <div>
                          <span className='caterer-summary-card__key'>Station: </span>
                          <strong>{previewResult.extractedData?.locationCode || '—'}</strong>
                        </div>
                      </div>
                    </div>

                    {outstandingFields.length > 0 && (
                      <div className='caterer-missing-fields'>
                        <div className='caterer-missing-fields__title'>
                          <FaExclamationTriangle size={16} /> Provide Missing Details:
                        </div>
                        {['invoiceNumber', 'locationCode'].filter(f => outstandingFields.includes(f)).map(renderPlainField)}
                        {renderCatererField()}
                        {renderDateRangeField()}
                        {['sector', 'poNumber'].filter(f => outstandingFields.includes(f)).map(renderPlainField)}
                      </div>
                    )}

                    {submitError && (
                      <div className='caterer-alert caterer-alert--error'>
                        <FaExclamationTriangle size={20} />
                        <div className='caterer-alert__desc'>{submitError}</div>
                      </div>
                    )}

                    <div className='flexRow justifyContentFlexEnd'>
                      <button
                        className={`done-button ${(!isReadyToSubmit || isSubmitting) ? 'button_inrt' : ''}`}
                        disabled={!isReadyToSubmit || isSubmitting}
                        onClick={handleSubmit}
                      >
                        <FaCheckCircle /> {isSubmitting ? 'Submitting...' : 'Submit for Verification'}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* RIGHT PANEL: PDF PREVIEW */}
              <div className='caterer-split__preview'>
                <div className='caterer-preview__header'>
                  <span>{selectedPdf ? selectedPdf.name : 'Document Preview'}</span>
                  {selectedPdf && (
                    <span className='flexRow alignItemsCenter gap10 pointer'>
                      <FaChevronCircleLeft size={16} onClick={decrementPageNo} />
                      Page {pageNumber} of {numPages || 1}
                      <FaChevronCircleRight size={16} onClick={incrementPageNo} />
                    </span>
                  )}
                </div>
                <div className='caterer-preview__body sm-scrollbar'>
                  {selectedPdf ? (
                    <CatererPdfViewer
                      fileHolder={selectedPdf}
                      numPages={numPages}
                      pageNumber={pageNumber}
                      setNumPages={setNumPages}
                      setPageNumber={setPageNumber}
                    />
                  ) : (
                    <div className='caterer-preview__empty'>
                      <FaFilePdf size={60} style={{ marginBottom: '12px' }} />
                      <div>Select a PDF to preview it here</div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
